import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CalendarCheck2, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const todayKey = () => new Date().toISOString().slice(0, 10);
const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const dateKey = (date) => date.toISOString().slice(0, 10);

const getLastDays = (count) => {
  const today = new Date();
  return Array.from({ length: count }, (_, index) => addDays(today, index - (count - 1)));
};

const getStreak = (habit) => {
  let streak = 0;
  let cursor = new Date();
  while (habit.logs?.[dateKey(cursor)]) {
    streak += 1;
    cursor = addDays(cursor, -1);
  }
  return streak;
};

const palette = ["#2563EB", "#059669", "#D97706", "#7C3AED", "#DB2777"];

const StudentHabitTracker = () => {
  const { user } = useAuth();
  const storageKey = `kampus-habits-${user?.uid || "guest"}`;
  const [habitName, setHabitName] = useState("");
  const [habits, setHabits] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch {
      return [];
    }
  });
  const days = useMemo(() => getLastDays(7), []);

  const saveHabits = (next) => {
    setHabits(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  };

  const addHabit = (event) => {
    event.preventDefault();
    const clean = habitName.trim();
    if (!clean) return;
    const next = [
      ...habits,
      {
        id: Date.now(),
        name: clean,
        color: palette[habits.length % palette.length],
        logs: {},
        createdAt: new Date().toISOString(),
      },
    ];
    saveHabits(next);
    setHabitName("");
  };

  const toggleHabit = (habitId, key) => {
    saveHabits(
      habits.map((habit) => {
        if (habit.id !== habitId) return habit;
        const logs = { ...(habit.logs || {}) };
        if (logs[key]) delete logs[key];
        else logs[key] = true;
        return { ...habit, logs };
      })
    );
  };

  const removeHabit = (habitId) => {
    saveHabits(habits.filter((habit) => habit.id !== habitId));
  };

  const completedToday = habits.filter((habit) => habit.logs?.[todayKey()]).length;

  return (
    <div data-testid="student-habit-tracker">
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/student/productivity" className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700">
            <ArrowLeft className="h-4 w-4" /> Productivity Tools
          </Link>
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Habit Tracker</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Track your daily habits</h1>
          <p className="mt-2 text-slate-600">Today: {completedToday}/{habits.length} completed</p>
        </div>
        <form onSubmit={addHabit} className="flex w-full max-w-xl gap-2">
          <input
            value={habitName}
            onChange={(event) => setHabitName(event.target.value)}
            placeholder="Add a habit, e.g. Read 10 minutes"
            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          />
          <button type="submit" className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>
      </div>

      {habits.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <CalendarCheck2 className="mx-auto h-10 w-10 text-blue-600" />
          <h2 className="mt-4 font-heading text-xl font-bold text-slate-900">No habits yet</h2>
          <p className="mt-2 text-slate-600">Add your first habit and start marking it daily.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {habits.map((habit) => (
            <div key={habit.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: habit.color }} />
                  <div>
                    <h2 className="font-heading text-lg font-bold text-slate-900">{habit.name}</h2>
                    <p className="text-xs font-semibold text-slate-500">{getStreak(habit)} day streak</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeHabit(habit.id)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600"
                  aria-label={`Delete ${habit.name}`}
                  title="Delete habit"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-2">
                {days.map((day) => {
                  const key = dateKey(day);
                  const done = !!habit.logs?.[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => toggleHabit(habit.id, key)}
                      className={`min-h-20 rounded-lg border p-2 text-center transition-all ${
                        done ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-white"
                      }`}
                    >
                      <div className="text-[11px] font-bold uppercase">{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                      <div className="mt-1 font-heading text-lg font-bold">{day.getDate()}</div>
                      <div className="mt-1 text-xs font-semibold">{done ? "Done" : "Mark"}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentHabitTracker;
